import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listReports, deleteReport, listInvoices, downloadPdf } from '../services/api';
import { openPdfInNewTab } from '../utils/openPdf';
import { formatDateTime, formatShortDate } from '../utils/dates';
import ListCard from '../components/ListCard';
import SendReportDialog, { sentNotice as noticeAfterSending } from '../components/SendReportDialog';
import useMediaQuery, { MOBILE_QUERY } from '../hooks/useMediaQuery';

function getSortValue(report, key) {
  switch (key) {
    case 'requesting_company':
      return report.requesting_company?.name?.toLowerCase() || '';
    case 'customer':
      return (report.client_name || report.customer?.name || '').toLowerCase();
    case 'author_name':
      return (report.author_name || '').toLowerCase();
    case 'title':
      return (report.title || '').toLowerCase();
    case 'status':
      return (report.status || '').toLowerCase();
    case 'created_at':
    default:
      return report.created_at || '';
  }
}


const envelopeIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" width="14" height="14" style={{ flex: 'none' }}>
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
  </svg>
);

const PAGE_SIZE = 25;

export default function ReportsPage({ currentUser }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [invoiceCounts, setInvoiceCounts] = useState({}); // report_id -> count
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [sendingId, setSendingId] = useState(null); // report whose e-mail dialog is open
  const [sentNotice, setSentNotice] = useState('');

  const isAdmin = currentUser?.role === 'admin';
  const isPhone = useMediaQuery(MOBILE_QUERY);

  // Server-side search + pagination (debounced).
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      listReports({ q: search || undefined, limit: PAGE_SIZE, offset })
        .then((d) => { setReports(d.items || []); setTotal(d.total || 0); })
        .catch(() => {})
        .finally(() => { setLoading(false); setFirstLoad(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, offset]);

  useEffect(() => {
    listInvoices()
      .then((invs) => {
        const counts = {};
        for (const inv of invs) {
          if (inv.report_id) counts[inv.report_id] = (counts[inv.report_id] || 0) + 1;
        }
        setInvoiceCounts(counts);
      })
      .catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    const copy = [...reports];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return copy;
  }, [reports, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(key === 'title' || key === 'author_name');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return ' \u2195';
    return sortAsc ? ' \u2191' : ' \u2193';
  };

  if (firstLoad) {
    return <p>{t('auth.loading')}</p>;
  }

  const trashIcon = (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );

  const handleViewPdf = async (report) => {
    setPdfError(null);
    setPdfBusyId(report.id);
    try {
      await openPdfInNewTab(() => downloadPdf(report.id), `rapport_${report.reference || report.id}.pdf`);
    } catch {
      setPdfError(t('reports.pdfFailed'));
    } finally {
      setPdfBusyId(null);
    }
  };

  const openSend = (report) => { setSentNotice(''); setSendingId(report.id); };

  // The row's chip follows the send without reloading the list.
  const handleSent = (sent) => {
    setReports((prev) => prev.map((r) => (r.id === sendingId ? { ...r, last_emailed_at: sent.sent_at, status: 'completed' } : r)));
    setSendingId(null);
    setSentNotice(noticeAfterSending(t, sent));
  };

  // "Sent 12/10" — the full date and time on hover; null if never sent.
  const sentLabel = (report) => (report.last_emailed_at
    ? t('sendReport.sentOn', {
        date: formatShortDate(report.last_emailed_at, i18n.language),
        // The date contains "/", which i18next would render as &#x2F;.
        interpolation: { escapeValue: false },
      })
    : null);
  const sentTitle = (report) => t('sendReport.sentOnTitle', {
    datetime: formatDateTime(report.last_emailed_at, i18n.language),
    interpolation: { escapeValue: false },
  });
  const sentChip = (report) => (report.last_emailed_at ? (
    <span className="sent-chip" title={sentTitle(report)}>{sentLabel(report)}</span>
  ) : null);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t('reports.deleteConfirm'))) return;
    await deleteReport(id);
    setReports((prev) => prev.filter((r) => r.id !== id));
  };

  const columns = [
    { key: 'created_at', label: t('reports.date') },
    { key: 'title', label: t('wizard.title') },
    ...(isAdmin ? [{ key: 'author_name', label: t('reports.author') }] : []),
    { key: 'requesting_company', label: t('reports.requestingCompany') },
    { key: 'customer', label: t('reports.intervention') },
    { key: 'status', label: t('admin.status') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{isAdmin ? t('reports.allReports') : t('reports.title')}</h1>
        <button className="btn btn-primary" onClick={() => navigate('/reports/new')}>
          + {t('reports.create')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="form-control"
          style={{ maxWidth: 360 }}
          placeholder={t('reports.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
        />
        {loading && <span style={{ fontSize: 13, color: 'var(--color-text-light)' }}>{t('auth.loading')}</span>}
      </div>

      {pdfError && (
        <div className="row-actions-error" role="alert">{pdfError}</div>
      )}

      {sentNotice && <p className="send-notice" role="status">{sentNotice}</p>}

      {reports.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128203;</div>
          <p>{search ? t('reports.noResults') : t('reports.empty')}</p>
        </div>
      ) : isPhone ? (
        <div className="list-cards">
          {sorted.map((report) => (
            <ListCard
              key={report.id}
              status={report.status}
              title={report.title}
              reference={report.reference}
              badge={
                <span className={`badge badge-${report.status}`}>
                  {t(`reports.${report.status}`)}
                </span>
              }
              meta={[
                new Date(report.created_at).toLocaleDateString(),
                report.requesting_company?.name || report.client_name || report.customer?.name,
                isAdmin ? report.author_name : null,
                sentChip(report),
              ]}
              onClick={() => navigate(`/reports/${report.id}`)}
              actions={
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={pdfBusyId === report.id}
                    onClick={() => handleViewPdf(report)}
                  >
                    {pdfBusyId === report.id ? t('reports.pdfOpening') : t('reports.viewPdf')}
                  </button>
                  <button className="btn btn-secondary btn-sm send-btn" onClick={() => openSend(report)}>
                    {envelopeIcon}{t('sendReport.button')}
                  </button>
                  {invoiceCounts[report.id] > 0 ? (
                    <button
                      className="btn btn-secondary btn-sm row-actions-count"
                      onClick={() => navigate(`/invoices?report=${report.id}`)}
                    >
                      {t('reports.viewInvoices', { count: invoiceCounts[report.id] })}
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm list-card-bill"
                      onClick={() => navigate(`/invoices?invoice_for=${report.id}`)}
                    >
                      {t('invoices.invoice')}
                    </button>
                  )}
                </>
              }
              destructive={
                <button
                  className="btn btn-ghost btn-sm"
                  title={t('reports.delete')}
                  aria-label={t('reports.delete')}
                  onClick={(e) => handleDelete(e, report.id)}
                >
                  {trashIcon}
                </button>
              }
            />
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="sortable"
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}{sortIcon(col.key)}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((report) => (
                  <tr
                    key={report.id}
                    className="clickable"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(report.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{report.title}</div>
                      {report.reference && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-light)', marginTop: 2 }}>
                          {report.reference}
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ fontSize: 13 }}>
                        {report.author_name || '\u2014'}
                      </td>
                    )}
                    <td style={{ fontSize: 13 }}>
                      {report.requesting_company?.name || '\u2014'}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {report.client_name || report.customer?.name || '\u2014'}
                    </td>
                    <td>
                      <span className={`badge badge-${report.status}`}>
                        {t(`reports.${report.status}`)}
                      </span>
                      {/* Under the badge, not in the rail: it is a state to read,
                          not a slot, so it cannot shift the buttons. */}
                      {report.last_emailed_at && <div>{sentChip(report)}</div>}
                    </td>
                    <td>
                      {/* Fixed slots: every action keeps its place whether or
                          not the report has invoices, so nothing slides under
                          a pointer already aimed at something else. */}
                      <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-secondary btn-sm"
                          title={t('reports.viewPdf')}
                          aria-label={t('reports.viewPdf')}
                          disabled={pdfBusyId === report.id}
                          onClick={() => handleViewPdf(report)}
                        >
                          {pdfBusyId === report.id ? t('reports.pdfOpening') : t('reports.pdf')}
                        </button>

                        <button
                          className="btn btn-secondary btn-sm send-btn"
                          title={t('sendReport.open')}
                          onClick={() => openSend(report)}
                        >
                          {envelopeIcon}{t('sendReport.button')}
                        </button>

                        <button
                          className="btn btn-accent btn-sm"
                          onClick={() => navigate(`/invoices?invoice_for=${report.id}`)}
                        >
                          {t('invoices.invoice')}
                        </button>

                        {invoiceCounts[report.id] > 0 ? (
                          <button
                            className="btn btn-secondary btn-sm row-actions-count"
                            title={t('reports.viewInvoicesTitle')}
                            onClick={() => navigate(`/invoices?report=${report.id}`)}
                          >
                            {t('reports.viewInvoices', { count: invoiceCounts[report.id] })}
                          </button>
                        ) : (
                          <span className="row-actions-empty" aria-hidden="true" />
                        )}

                        <button
                          className="btn btn-danger btn-sm row-actions-danger"
                          onClick={(e) => handleDelete(e, report.id)}
                        >
                          {t('reports.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sendingId && (
        <SendReportDialog
          reportId={sendingId}
          onSent={handleSent}
          onClose={() => setSendingId(null)}
        />
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            ← {t('reports.prev')}
          </button>
          <span style={{ fontSize: 13, color: 'var(--color-text-light)' }}>
            {t('reports.pageInfo', { from: offset + 1, to: Math.min(offset + PAGE_SIZE, total), total })}
          </span>
          <button className="btn btn-secondary btn-sm" disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}>
            {t('reports.next')} →
          </button>
        </div>
      )}
    </div>
  );
}
